import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  type ElementFormState,
  InputElementMetaUiFeatures,
  makeCheckboxAlignStyles,
} from '@blaze-shared/elements-domain';
import { isDatasetLinkValue } from '@blaze-shared/schema-domain';
import { BlazeCheckboxChange, CheckboxComponent, safeDetectChanges } from '@blaze-shared/shared-ui';
import { isNullish, useEmitTracker } from '@blaze-shared/shared-util';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { CanvasElementBaseComponent } from '../__base/base.component';

@UntilDestroy()
@Component({
  selector: 'be-checkbox-input',
  templateUrl: './checkbox-input.component.html',
  styleUrls: ['./checkbox-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckboxInputComponent
  extends CanvasElementBaseComponent<'checkboxInput'>
  implements CanvasElementComponent<'checkboxInput'>, OnInit, OnChanges, OnDestroy
{
  @Input() value: boolean | undefined = false;
  @Input() formState?: ElementFormState;
  @ViewChild('checkbox', { static: false }) checkboxRef?: CheckboxComponent;

  @HostBinding('style.--b-col-bool-on-bg') colBoolOnBg = FALLBACK_THEME.value.accent;
  @HostBinding('style.--b-col-bool-on-mark') colBoolOnMark = FALLBACK_THEME.value.fill;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly schemaUpdateTracker = useEmitTracker();

  readonly elementKeyRoot = 'b-chi';
  elementKey!: string;

  labelEnabled!: boolean;
  labelText!: string;
  isReadonly!: boolean;

  styles!: Record<string, string>;
  inputStyles!: Record<string, string>;
  validationStyles!: Record<string, string>;

  get hasValidationError(): boolean | undefined {
    return this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  get titlePosition(): string | undefined {
    return this.props?.content.title?.position ?? 'left';
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      }
      const { title } = this.props?.content || {};

      this.labelEnabled = title?.enabled ?? true;

      if (this.schemaUpdateTracker.shouldEmit(this.getEventSchema('change'))) {
        this.setDefaultEventSchema('change');
      }

      if (!this.bindingResults?.['value']) {
        this.updateOutput(false);
      }

      this.updateStyles();
    }

    if (changes?.['value']) {
      this.processValue();
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);

        this.isReadonly = features.isReadOnly || this.props?.content?.input?.readonly || false;

        safeDetectChanges(this.cdr);
      });

    this.stylesCleanup.register(this.elementKey);
  }

  ngOnDestroy(): void {
    this.stylesCleanup.dispose(this.elementKey);
  }

  onChange(event: BlazeCheckboxChange): void {
    this.updateOutput(event.checked);
  }

  reset(): void {
    this.outputEmitTracker.reset();
    this.processValue();
  }

  private processValue(): void {
    if (isDatasetLinkValue(this.value)) {
      this.value = Boolean(this.value.display);
    } else if (!isNullish(this.value)) {
      this.value = Boolean(this.value);
    }
    if (this.checkboxRef) {
      this.checkboxRef.checked = this.value ?? false;
    }
    this.updateOutput(this.value);
  }

  private updateOutput(data: boolean | undefined): void {
    this.value = data;
    // The value is missing, so we don't emit anything
    if (data === undefined) {
      return;
    }

    const schema = this.getEventSchema('change');
    if (!schema) {
      return;
    }

    const isInitialData = !this.outputEmitTracker.hasPrevious();
    if (!this.outputEmitTracker.shouldEmit(data)) {
      return;
    }

    this.output.emit({
      eventType: 'change',
      data,
      isInitialData,
      schema,
    });
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title } = content || {};
    const { general, input, labelBlock } = style || {};
    if (!style || !title || !general || !input || !labelBlock) {
      return;
    }

    const styles: Record<string, string> = {};
    let inputStyles: Record<string, string> = {};
    const validationStyles: Record<string, string> = {};

    if (title.position === 'top') {
      styles['flex-direction'] = `column`;
      inputStyles = {
        ...inputStyles,
        ...makeCheckboxAlignStyles(title.align),
      };
    } else if (title.enabled !== false) {
      if (title.position === 'left') {
        validationStyles['margin-left'] = title.width;
        validationStyles['padding-left'] = '8px';
      }
      inputStyles['flex-basis'] = `${100 - parseInt(title.width, 10)}%`;
      inputStyles['margin-left'] = 'auto';
    }

    if (title.position === 'right') {
      styles['flex-direction'] = `row-reverse`;
    }
    if (
      title.position === 'left' ||
      (title.position === 'right' &&
        !this.isUsingOldPaddingStrategy &&
        !this.elementContext?.extendInputPadding)
    ) {
      styles['gap'] = '8px';
    }

    this.styles = styles;

    this.inputStyles = inputStyles;
    this.validationStyles = validationStyles;

    this.colBoolOnBg = general.accent.color1;
    this.colBoolOnMark = input.symbol.color1;

    this.stylesCleanup.update(
      this.elementKey,
      `
      blaze-checkbox.${this.elementKey}.bat-checkbox input[type='checkbox'] {
        background-color: ${input.fill.color1};
        border-radius: ${input.radius};
        border: 1px solid ${input.border.selectorType === 'none' ? 'transparent' : input.border.color1};
      }
      blaze-checkbox.${this.elementKey}.bat-checkbox input[type='checkbox']:checked {
        background-color: ${general.accent.color1};
        border-color: ${general.accent.color1};
      }
      blaze-checkbox.${this.elementKey}.bat-checkbox .blaze-checkbox-check-mark {
        color: ${input.symbol.color1};
      }
    `,
    );
  }
}
